// packages/web/src/pages/AboutPage.tsx
import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Header } from '../components/Layout/Header';
import { Footer } from '../components/Layout/Footer';
import { LoginDialog } from '../components/Auth/LoginDialog';
import { useAuth } from '../hooks/useAuth';

const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: '#ffffff',
});

const HeroSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#fafafa',
  padding: theme.spacing(8, 0, 6, 0),
  textAlign: 'center',
  borderBottom: '1px solid #e5e5e5',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 0, 4, 0),
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 700,
  lineHeight: 1.2,
  marginBottom: theme.spacing(2),
  color: '#1a1a1a',
  letterSpacing: '-0.02em',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  color: '#666',
  lineHeight: 1.7,
  fontWeight: 400,
  maxWidth: '700px',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 0),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  marginBottom: theme.spacing(3),
  color: '#1a1a1a',
  letterSpacing: '-0.01em',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.75rem',
  },
}));

const BodyText = styled(Typography)(({ theme }) => ({
  fontSize: '1.0625rem',
  color: '#666',
  lineHeight: 1.8,
  fontWeight: 400,
  marginBottom: theme.spacing(2.5),
}));

const HighlightBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#f0f8e8',
  padding: theme.spacing(4),
  borderRadius: '16px',
  border: '1px solid #c4d600',
  marginTop: theme.spacing(5),
  marginBottom: theme.spacing(5),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const HighlightText = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  color: '#1a1a1a',
  lineHeight: 1.7,
  fontWeight: 500,
  textAlign: 'center',
  fontStyle: 'italic',
}));

const ValueCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
}));

const ValueTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 700,
  marginBottom: theme.spacing(1.5),
  color: '#84b817',
  letterSpacing: '-0.01em',
}));

const ValueDescription = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666',
  lineHeight: 1.7,
  fontWeight: 400,
}));

const AboutPage: React.FC = () => {
  const { user, signOut } = useAuth();
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  const values = [
    {
      title: 'Simplicity First',
      description: 'Tennis scheduling should be effortless. We focus on making it easy to share your availability and find matches without unnecessary complexity.',
    },
    {
      title: 'Community Driven',
      description: 'We believe in building a supportive tennis community where players can easily connect and enjoy more time on the court together.',
    },
    {
      title: 'Always Free',
      description: 'Great tennis shouldn\'t cost extra. Our platform will always be free for players to coordinate matches and grow the game.',
    },
  ];

  return (
    <PageContainer>
      <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />

      <HeroSection>
        <Container maxWidth="md">
          <PageTitle>About Sched Tennis</PageTitle>
          <PageSubtitle>
            Making it easier for tennis players to find matches and spend more time on the court
          </PageSubtitle>
        </Container>
      </HeroSection>

      <ContentSection>
        <Container maxWidth="md">
          <SectionTitle>Our Mission</SectionTitle>
          <BodyText>
            Sched Tennis was created to solve a simple but frustrating problem: coordinating tennis matches 
            with other players shouldn't require endless back-and-forth messages, phone calls, and scheduling 
            conflicts.
          </BodyText>
          <BodyText>
            We believe that the hardest part of playing tennis should be the match itself, not finding 
            someone to play with. That's why we built a platform where you can publish your availability, 
            see when others are free, and coordinate matches instantly.
          </BodyText>

          <HighlightBox>
            <HighlightText>
              "Life's too short for scheduling hassles. Let's get back to playing tennis."
            </HighlightText>
          </HighlightBox>

          <SectionTitle>How It Started</SectionTitle>
          <BodyText>
            Like many tennis players, we found ourselves spending more time trying to organize matches 
            than actually playing them. Group chats would fill with "When can you play?" messages, and 
            coordinating with multiple people was nearly impossible.
          </BodyText>
          <BodyText>
            We realized what we needed was a way to simply show when we're available and let others 
            see that information. No complex booking systems, no payments, no unnecessary features—just 
            a straightforward way to share schedules and find matches.
          </BodyText>
          <BodyText>
            So we built Sched Tennis, and we're excited to share it with the tennis community.
          </BodyText>

          <Box sx={{ mt: 8, mb: 4 }}>
            <SectionTitle>What We Value</SectionTitle>
          </Box>
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} md={4} key={index}>
                <ValueCard>
                  <ValueTitle>{value.title}</ValueTitle>
                  <ValueDescription>{value.description}</ValueDescription>
                </ValueCard>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 8 }}>
            <SectionTitle>Join Us</SectionTitle>
            <BodyText>
              Whether you're a casual player looking for weekend matches or a competitive player 
              seeking regular practice partners, Sched Tennis is here to help you spend less time 
              coordinating and more time playing.
            </BodyText>
            <BodyText>
              Join our growing community of tennis players and start sharing your schedule today!
            </BodyText>
          </Box>
        </Container>
      </ContentSection>

      <Footer />

      <LoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
      />
    </PageContainer>
  );
};

export default AboutPage;
